// CustomerLab.jsx — مختبر تجربة العميل
//
// Single page consolidating:
//   1. Auto-segmentation for a chosen date range (4 buckets)
//   2. 24×7 hour-by-hour revenue heatmap
//   3. Smart campaign suggestions that EXCLUDE hours/segments already
//      covered by an active campaign
//   4. One-click "Launch as Campaign" / "Send WhatsApp Blast" — wires
//      into the existing /api/campaigns + /api/campaigns/blast endpoints,
//      so nothing on the dashboard goes out of sync.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./lib/api";
import { showToast } from "./lib/theme";
import DateField from "./components/DateField";
import MetricExplainer from "./customer_intelligence/MetricExplainer";
// Phase 7h — فلتر الفرع الموحّد (نفس مكوّن بطاقات ذكاء العملاء، بلا نسخة ثانية).
import BranchSelect from "./customer_intelligence/BranchSelect";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";
import { ksaDaysAgo, ksaToday } from "./lib/ksaTime";
import { polarityColor } from "./CompareTable";

// helper — match the showToast signature used across the app
const toast = (message, kind = "success") => showToast({ kind: kind === "error" ? "danger" : kind, message });

// 30-day window ending today, used as default range
const _today = () => ksaToday();
const _daysAgo = (n) => ksaDaysAgo(n);

const fm = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// ─── Shared: yellow/gold explainer banner used at the top of every lab tab ───
function LabBanner({ title, lines, actionHint }) {
  return (
    <div data-testid="lab-banner" style={{
      background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
      border: "1px solid #f59e0b", borderRadius: 12,
      padding: 14, marginBottom: 14, fontSize: 13, lineHeight: 1.7, color: "#78350f",
    }}>
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>💡 {title}</div>
      {(lines || []).map((line, i) => (
        <div key={i} style={{ marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: line }} />
      ))}
      {actionHint && (
        <div style={{ marginTop: 4, fontWeight: 700 }}>
          <span style={{ background: "#fbbf24", color: "#78350f", padding: "1px 8px", borderRadius: 4 }}>الإجراء</span>{" "}
          <span dangerouslySetInnerHTML={{ __html: actionHint }} />
        </div>
      )}
    </div>
  );
}

// ─── Shared: coverage-aware action button ───
function ActionLaunchButton({ coverageKey, coverage, onClick, label = "📩 إنشاء حملة", testid }) {
  const conflict = coverage?.coverage_keys?.[coverageKey];
  if (conflict) {
    return (
      <span
        data-testid={`${testid}-covered`}
 
        style={{
          fontSize: 11, padding: "4px 8px", borderRadius: 6,
          background: "#dbeafe", color: "#1e3a8a", fontWeight: 700, cursor: "help",
          display: "inline-block",
        }}
      ><InfoTip text={`مغطّى بحملة نشطة: ${conflict.campaign_name}`} />
        ℹ️ مغطّى بحملة نشطة
      </span>
    );
  }
  return (
    <button
      data-testid={testid}
      className="btn btn-primary"
      style={{ fontSize: 11, padding: "4px 8px" }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default function CustomerLab() {
  const [dateFrom, setDateFrom] = useState(_daysAgo(30));
  const [dateTo, setDateTo] = useState(_today());
  const [view, setView] = useState("overview"); // overview | compare | clusters | dom | losses | atv
  const [segments, setSegments] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [lab, setLab] = useState({ compare: null, clusters: null, dom: null, losses: null, atv: null, shifts: null, scorecard: null });
  const [busy, setBusy] = useState(false);
  const [coverage, setCoverage] = useState(null);
  // Phase 7h — فلتر فرع اختياري للمختبر (يُطبَّق فقط على التبويبات الداعمة:
  // التجزئة + خريطة الساعات). القائمة تأتي من نفس مصدر بطاقات ذكاء العملاء.
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  // Phase 9 — hybrid source toggle for the two "temporal pattern" screens.
  // Persist per-screen so switching tabs doesn't reset the choice.
  const [labSource, setLabSource] = useState({ clusters: "live", dom: "live" });

  // Compare period inputs (second period defaults to "the 30 days before A")
  const [bFrom, setBFrom] = useState(_daysAgo(60));
  const [bTo, setBTo] = useState(_daysAgo(31));

  const refreshCoverage = useCallback(async () => {
    try {
      const r = await api.get("/customer-lab/active-coverage");
      setCoverage(r.data);
    } catch (_e) { /* silent — covered checks become permissive */ }
  }, []);
  useEffect(() => { refreshCoverage(); }, [refreshCoverage]);

  // Phase 7h — نجلب قائمة الفروع من نفس مصدر بطاقات ذكاء العملاء
  // (repeat-rate يُرجع branches: [{id, name}]) لملء عنصر BranchSelect.
  useEffect(() => {
    api.get("/customers/repeat-rate")
      .then((r) => setBranches(r.data?.branches || []))
      .catch(() => setBranches([]));
  }, []);

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setBusy(true);
    try {
      const branch = branchId || undefined;
      const [seg, hm] = await Promise.all([
        api.post("/customer-lab/segments", { date_from: dateFrom, date_to: dateTo, branch_id: branch }),
        api.post("/customer-lab/hour-heatmap", { date_from: dateFrom, date_to: dateTo, branch_id: branch }),
      ]);
      setSegments(seg.data); setHeatmap(hm.data);
    } catch (e) {
      toast(e?.response?.data?.detail || "تعذّر التحميل", "error");
    } finally { setBusy(false); }
  }, [dateFrom, dateTo, branchId]);

  const loadLab = useCallback(async (kind) => {
    setBusy(true);
    try {
      let res;
      if (kind === "compare") {
        res = await api.post("/customer-lab/compare-periods",
          { a_from: bFrom, a_to: bTo, b_from: dateFrom, b_to: dateTo });
      } else {
        const ep = { clusters: "/customer-lab/visit-clusters",
                     dom: "/customer-lab/day-of-month",
                     losses: "/customer-lab/lost-first-impressions",
                     atv: "/customer-lab/atv-by-hour",
                     shifts: "/customer-lab/shift-performance",
                     scorecard: "/customer-lab/employee-scorecard" }[kind];
        // Phase 9 — for the two hybrid screens, forward the current source
        // choice. Others ignore the field.
        const body = { date_from: dateFrom, date_to: dateTo };
        if (kind === "clusters" || kind === "dom") {
          body.source = labSource[kind] || "live";
        }
        res = await api.post(ep, body);
      }
      setLab((l) => ({ ...l, [kind]: res.data }));
    } catch (e) {
      toast(e?.response?.data?.detail || "تعذّر التحميل", "error");
    } finally { setBusy(false); }
  }, [dateFrom, dateTo, bFrom, bTo, labSource]);

  // Phase 9 — auto-refetch when the user flips the source toggle.
  const setSourceFor = useCallback((kind, src) => {
    setLabSource((s) => ({ ...s, [kind]: src }));
    setLab((l) => ({ ...l, [kind]: null }));   // force re-fetch
  }, []);

  useEffect(() => { load(); }, [load]);
  // Auto-fetch lab data when user switches to a lab tab the first time
  useEffect(() => {
    if (view !== "overview" && !lab[view]) loadLab(view);
  }, [view, lab, loadLab]);

  return (
    <div className="space-y-4" data-testid="customer-lab" style={{ direction: "rtl" }}>
      <div className="card">
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22 }}>🧪 مختبر تجربة العميل</h2>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
              تجزئة + خريطة ساعات + تحليلات سلوكية عميقة. الاقتراحات الذكية انتقلت إلى مركز الحملات ← تبويب «التوصيات الذكية».
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <BranchSelect
              value={branchId}
              onChange={setBranchId}
              branches={branches}
              testId="lab-branch-filter"
              label="الفرع (اختياري)"
              tooltip="يُطبَّق الفلتر على التجزئة وخريطة الساعات فقط. «كل الفروع» = بدون فلتر."
            />
            <label style={{ fontSize: 12 }}>من:
              <DateField  value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                data-testid="lab-date-from"
                style={{ marginInlineStart: 6, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6 }} />
            </label>
            <label style={{ fontSize: 12 }}>إلى:
              <DateField  value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                data-testid="lab-date-to"
                style={{ marginInlineStart: 6, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6 }} />
            </label>
            <button onClick={load} disabled={busy} data-testid="lab-refresh"
              style={{ padding: "6px 14px", borderRadius: 6, background: "var(--accent)", color: "#fff", border: "none", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}>
              {busy ? "⏳ يحلّل…" : "🔄 تحليل"}
            </button>
          </div>
        </div>
      </div>

      {/* Sub-tab nav */}
      <div className="card" style={{ padding: 6 }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {[
            { id: "overview", label: "🎯 نظرة عامة" },
            { id: "compare",  label: "🆚 مقارنة فترتين" },
            { id: "atv",      label: "💰 متوسط الفاتورة/ساعة" },
            { id: "dom",      label: "📅 نمط الشهر (رواتب)" },
            { id: "clusters", label: "👥 تجمعات الزيارة" },
            { id: "losses",   label: "💔 فقدان الانطباع الأول" },
            { id: "shifts",   label: "🧑‍🍳 أداء الورديات" },
            { id: "ab",       label: "📦 اختبار A/B للقوالب" },
          ].map((t) => (
            <button key={t.id} type="button" onClick={() => setView(t.id)}
              data-testid={`lab-view-${t.id}`}
              style={{
                padding: "6px 12px", borderRadius: 6, border: "none",
                background: view === t.id ? "var(--accent)" : "transparent",
                color: view === t.id ? "#fff" : "var(--text)",
                fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {view === "overview" && (<>
        <LabBanner
          title="نظرة عامة على مختبر العملاء"
          lines={[
            "هذا التبويب يجمع لك <b>طبقتَي تحليل سريع</b>: تجزئة (الأبطال / المكتشفون / العابرون / الحذرون) + خريطة إيراد بالساعة.",
            "<b>فلتر الفرع</b> بالأعلى يطبّق على التجزئة وخريطة الساعات — اختر فرعًا لتحليل أداءه وحده أو «كل الفروع» للصورة الشاملة.",
          ]}
          actionHint="الاقتراحات الذكية للحملات انتقلت إلى <b>مركز الحملات ← تبويب «التوصيات الذكية»</b>. لتحليل أعمق هنا، انتقل لتبويبات: تجمعات، فقدان، Upsell، نمط الشهر."
        />
        {segments && <SegmentsSection data={segments} />}
        {heatmap && <HeatmapSection data={heatmap} />}
      </>)}
      {view === "compare" && (
        <CompareView data={lab.compare} bFrom={bFrom} bTo={bTo}
          setBFrom={setBFrom} setBTo={setBTo}
          aFrom={dateFrom} aTo={dateTo}
          onRun={() => loadLab("compare")} busy={busy} />
      )}
      {view === "atv" && <AtvView data={lab.atv} coverage={coverage} onLaunched={refreshCoverage} />}
      {view === "dom" && <DomView data={lab.dom} coverage={coverage} onLaunched={refreshCoverage}
                                    source={labSource.dom}
                                    onSourceChange={(s) => setSourceFor("dom", s)} />}
      {view === "clusters" && <ClustersView data={lab.clusters} coverage={coverage} onLaunched={refreshCoverage}
                                              source={labSource.clusters}
                                              onSourceChange={(s) => setSourceFor("clusters", s)} />}
      {view === "losses" && <LossesView data={lab.losses} coverage={coverage} onLaunched={refreshCoverage} />}
      {view === "shifts" && <ShiftsView data={lab.shifts} />}
      {view === "ab" && <ABStatsView />}
    </div>
  );
}

/* ─────────── A/B Test Stats (v119.63) ─────────── */
function ABStatsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/customer-lab/ab-stats"); setData(r.data); }
      catch (e) { toast(e?.response?.data?.detail || "تعذّر التحميل", "error"); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="card" style={{ padding: 20, textAlign: "center" }}>⏳ ...</div>;
  const camps = data?.campaigns || [];
  return (
    <div className="card" data-testid="lab-ab-stats">
      <h3 style={{ marginTop: 0 }}>📦 اختبار A/B للقوالب</h3>
      <LabBanner title="وش هذا؟" lines={[
        "لما ترسل حملة عبر <b>قالبين أو أكثر</b> في نفس الوقت، النظام يقسم المستلمين 50/50 بينهم ويتتبع <b>أيهم يحقق قراءة وردود أكثر</b>.",
        "بعد جمع ≥30 رسالة لكل نسخة، الذكاء الاصطناعي يعلن <b>الفائز</b> بفارق ≥5 نقاط (60% للقراءة + 40% للرد).",
        "<b>طريقة الاستخدام:</b> أضف <code>template_variants: [\"name_v1\", \"name_v2\"]</code> لاقتراحك قبل التنفيذ — تلقائياً يفعّل A/B."
      ]} actionHint="عند توفر فائز واضح، استخدم القالب الفائز في حملاتك القادمة وأرشف الخاسر." />
      {camps.length === 0 ? (
        <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
          لا توجد حملات A/B بعد. أضف <code>template_variants</code> لاقتراح وأطلق execute-suggestion لبدء أول اختبار.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {camps.map((c) => (
            <div key={c.campaign_id} style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "#fff" }} data-testid={`ab-camp-${c.campaign_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 800 }}>{c.campaign_name}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>{c.recipients_total} مستلم · {(c.created_at || "").slice(0, 10)}</div>
                </div>
                {c.winner ? (
                  <span style={{ padding: "4px 10px", background: "#16a34a", color: "#fff", borderRadius: 12, fontSize: 11, fontWeight: 800 }}>🏆 الفائز: {c.winner}</span>
                ) : (
                  <span style={{ padding: "4px 10px", background: "#f3f4f6", color: "var(--muted)", borderRadius: 12, fontSize: 11 }}>⏳ بيانات غير كافية</span>
                )}
              </div>
              <table style={{ width: "100%", fontSize: 11.5 }}>
                <thead style={{ background: "var(--beige)" }}>
                  <tr>
                    <th style={{ padding: 6, textAlign: "start" }}>القالب</th>
                    <th style={{ padding: 6 }}>أُرسلت</th>
                    <th style={{ padding: 6 }}>قُرئت</th>
                    <th style={{ padding: 6 }}>ردّ</th>
                    <th style={{ padding: 6 }}>معدّل القراءة</th>
                    <th style={{ padding: 6 }}>معدّل الرد</th>
                    <th style={{ padding: 6 }}>النقاط</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.variants || []).map((v) => (
                    <tr key={v.variant} style={{ borderTop: "1px solid var(--border)", background: c.winner === v.variant ? "rgba(22,163,74,.08)" : "transparent" }}>
                      <td style={{ padding: 6 }}><code style={{ fontSize: 10 }}>{v.variant}</code></td>
                      <td style={{ padding: 6, textAlign: "center" }}>{v.sent}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>{v.read}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>{v.replied}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>{v.read_rate_pct}%</td>
                      <td style={{ padding: 6, textAlign: "center" }}>{v.reply_rate_pct}%</td>
                      <td style={{ padding: 6, textAlign: "center", fontWeight: 800 }}>{v.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── LAB: Compare Two Periods ─────────── */
function CompareView({ data, aFrom, aTo, bFrom, bTo, setBFrom, setBTo, onRun, busy }) {
  return (
    <div className="card" data-testid="lab-compare">
      <h3 style={{ marginTop: 0 }}>🆚 مقارنة فترتين</h3>
      <LabBanner
        title="وش هذي الصفحة؟"
        lines={[
          "تقارن لك <b>فترتين زمنيتين</b> جنباً إلى جنب لكشف <b>الاتجاهات</b>: هل عدد العملاء يرتفع؟ هل الإنفاق ينخفض؟ هل العملاء يتحوّلون من شريحة لأخرى؟",
          "أمثلة قابلة للتنفيذ: لو شريحة «الأوفياء» انخفض عددها 20%، فهذا تحذير مبكر قبل ما تتحول لـ«فقدناهم». لو الإنفاق الإجمالي ارتفع لكن نسبة العودة انخفضت، فأنت تكسب عملاء عابرين بدون ولاء.",
        ]}
        actionHint="هذا التبويب <b>تحليلي</b> لكشف الاتجاهات. للإجراءات الفعلية على شرائح بعينها، استخدم <b>تجمعات الزيارة</b> أو <b>فقدان الانطباع الأول</b> أو <b>نمط الشهر</b>."
      />
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
        الفترة الحالية (B) = من النموذج العلوي ({aFrom} → {aTo}). اختر فترة مرجعية للمقارنة (A):
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ fontSize: 12 }}>الفترة المرجعية من:
          <DateField  value={bFrom} onChange={(e) => setBFrom(e.target.value)} data-testid="compare-bfrom"
            style={{ marginInlineStart: 6, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6 }} />
        </label>
        <label style={{ fontSize: 12 }}>إلى:
          <DateField  value={bTo} onChange={(e) => setBTo(e.target.value)} data-testid="compare-bto"
            style={{ marginInlineStart: 6, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6 }} />
        </label>
        <button onClick={onRun} disabled={busy} data-testid="compare-run"
          style={{ padding: "6px 14px", borderRadius: 6, background: "var(--accent)", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
          🆚 قارن
        </button>
      </div>
      {!data ? <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>اختر فترتين واضغط «قارن»</div> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
            {Object.entries(data.kpi_deltas).map(([k, d]) => {
              const labels = { customers: "عملاء فريدون", invoices: "فواتير", spent: "إجمالي الإنفاق", repeat_rate_pct: "نسبة العودة %" };
              const positive = d.delta > 0;
              const kpiColor = polarityColor(d.delta, "higher_is_better");
              return (
                <div key={k} style={{ padding: 10, borderRadius: 8, background: "var(--beige)", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>{labels[k]}</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{fm(d.from)} → <b>{fm(d.to)}</b></div>
                  <div style={{ marginTop: 4, fontSize: 14, fontWeight: 800, color: kpiColor }}>
                    {positive ? "▲" : "▼"} {d.pct != null ? `${d.pct > 0 ? "+" : ""}${d.pct}%` : "—"}
                  </div>
                </div>
              );
            })}
          </div>
          <h4>حركة الـsegments:</h4>
          <div className="tw">
            <table>
              <thead><tr><th>الفئة</th><th>قبل (عدد)</th><th>بعد (عدد)</th><th>التغيير</th><th>إنفاق قبل</th><th>إنفاق بعد</th></tr></thead>
              <tbody>
                {data.bucket_diffs.map((b) => (
                  <tr key={b.key}>
                    <td>{b.label}</td>
                    <td>{fm(b.count.from)}</td>
                    <td>{fm(b.count.to)}</td>
                    <td style={{ color: polarityColor(b.count.delta, "higher_is_better"), fontWeight: 700 }}>
                      {b.count.delta > 0 ? "+" : ""}{b.count.delta} ({b.count.pct != null ? `${b.count.pct > 0 ? "+" : ""}${b.count.pct}%` : "—"})
                    </td>
                    <td>{fm(b.spent.from)} ر</td>
                    <td>{fm(b.spent.to)} ر</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────── LAB: ATV by Hour ─────────── */
function AtvView({ data, coverage, onLaunched }) {
  const [openCtx, setOpenCtx] = useState(null);
  if (!data) return <div className="card">جاري التحليل…</div>;
  const maxAtv = Math.max(...data.hours.map((h) => h.avg || 0), 1);
  return (
    <div className="card" data-testid="lab-atv">
      <h3 style={{ marginTop: 0 }}>💰 متوسط الفاتورة بالساعة</h3>
      <LabBanner
        title="وش هذي الصفحة؟"
        lines={[
          "تقيس <b>كم متوسط الفاتورة (ATV)</b> في كل ساعة. الساعات اللي ATV فيها عالي = جمهور يدفع أكثر، فرصة <b>upsell</b> (bundle حلى/مشروب ثاني).",
          "كذلك توزّع لك الفواتير على 3 شرائح: <b>≤15 ر = مشروب واحد</b> · <b>16-30 ر = + إضافة</b> · <b>+30 ر = طلب جماعي</b>. لو ساعة فيها 60% مشروب واحد، عندك فرصة ضخمة لـbundle.",
        ]}
        actionHint="اضغط <b>📩 حملة Upsell</b> لساعات الـATV العالي لإرسال bundle مقترح لزوار تلك الساعة. الساعات المغطّاة بحملة نشطة تظهر مخفية تلقائياً."
      />
      <div className="tw" style={{ maxHeight: 480, overflow: "auto" }}>
        <table>
          <thead><tr><th>ساعة</th><th>عدد الفواتير</th><th>ATV</th><th>منحنى</th><th>مشروب واحد</th><th>+ إضافة</th><th>طلب جماعي</th><th>إجراء</th></tr></thead>
          <tbody>
            {data.hours.filter((h) => h.n > 0).map((h) => {
              const coverageKey = `atv:${String(h.hour).padStart(2, "0")}`;
              const isHigh = h.avg >= maxAtv * 0.7;
              return (
                <tr key={h.hour}>
                  <td><b>{String(h.hour).padStart(2, "0")}:00</b></td>
                  <td>{fm(h.n)}</td>
                  <td style={{ fontWeight: 700, color: h.avg >= maxAtv * 0.85 ? "var(--success)" : h.avg <= maxAtv * 0.5 ? "var(--warn)" : "" }}>{h.avg} ر</td>
                  <td style={{ minWidth: 80 }}>
                    <div style={{ background: "#e5e7eb", borderRadius: 4, height: 12, overflow: "hidden" }}>
                      <div style={{ background: "var(--accent)", height: "100%", width: `${(h.avg / maxAtv * 100).toFixed(0)}%` }} />
                    </div>
                  </td>
                  <td>{h.single_pct}%</td>
                  <td>{h.addon_pct}%</td>
                  <td>{h.group_pct}%</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {isHigh && (h.phones?.length || 0) >= 3 ? (
                      <ActionLaunchButton
                        coverageKey={coverageKey}
                        coverage={coverage}
                        testid={`atv-campaign-${h.hour}`}
                        label="📩 حملة Upsell"
                        onClick={() => setOpenCtx({
                          kind: "atv_upsell",
                          coverage_key: coverageKey,
                          coverage_label: `Upsell ${String(h.hour).padStart(2, "0")}:00`,
                          title: `📩 حملة Upsell — الساعة ${String(h.hour).padStart(2, "0")}:00`,
                          subtitle: `${h.phones?.length || 0} عميل · ATV ${h.avg} ر · ${h.single_pct}% منهم يطلبون مشروب واحد فقط`,
                          phones: h.phones || [],
                          ai_ctx: { hour: h.hour, atv: h.avg, single_pct: h.single_pct, addon_pct: h.addon_pct },
                          default_message_builder: () => atvDefaultMsg(h.hour),
                        })}
                      />
                    ) : (
                      <span style={{ fontSize: 10, color: "var(--muted)" }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {openCtx && <LaunchCampaignModal ctx={openCtx} onClose={() => setOpenCtx(null)} onLaunched={onLaunched} />}
    </div>
  );
}

/* ─────────── LAB: Day-of-month Pattern ─────────── */
/* ─────────── Phase 9: source toggle (Live vs Historical Excel) ─────────── */
function LabSourceToggle({ source, onChange, meta }) {
  const [showExplain, setShowExplain] = useState(false);
  const isHist = source === "historical";
  const uploadDate = meta?.last_upload_at
    ? String(meta.last_upload_at).slice(0, 10)
    : null;
  return (
    <div
      data-testid="lab-source-toggle"
      style={{
        display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
        padding: "10px 12px", marginBottom: 10,
        background: isHist ? "rgba(217,119,6,0.08)" : "rgba(16,185,129,0.08)",
        border: `1px solid ${isHist ? "rgba(217,119,6,0.35)" : "rgba(16,185,129,0.35)"}`,
        borderRadius: 10, fontSize: 12,
      }}
    >
      <span style={{ fontWeight: 700, color: isHist ? "#b45309" : "#047857" }}>
        {isHist ? "📄 عرض تاريخي (Excel)" : "🟢 بيانات حيّة (RETM)"}
      </span>
      {isHist && uploadDate && (
        <span
          data-testid="lab-source-upload-date"
          style={{ color: "#78716c", fontSize: 11 }}
        >
          آخر رفع: {uploadDate}
        </span>
      )}
      {!isHist && (
        <span style={{ color: "#0f766e", fontSize: 11 }}>
          {meta?.row_count ? `${meta.row_count.toLocaleString("ar")} فاتورة حيّة` : "—"}
        </span>
      )}
      <button
        type="button"
        aria-label="ما الفرق بين المصدرين؟"
        data-testid="lab-source-explain-btn"
        onClick={() => setShowExplain(true)}
 
        style={{
          border: "none", cursor: "pointer",
          width: 22, height: 22, borderRadius: 999,
          background: "rgba(255,255,255,0.75)",
          color: isHist ? "#b45309" : "#047857",
          fontSize: 12, fontWeight: 800, lineHeight: 1,
        }}
      >?</button>
      <div style={{ marginInlineStart: "auto", display: "flex", gap: 6 }}>
        <button
          type="button"
          data-testid="lab-source-live-btn"
          onClick={() => onChange("live")}
          disabled={!isHist}
          style={{
            padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
            border: "1px solid var(--border)",
            background: !isHist ? "#047857" : "#fff",
            color: !isHist ? "#fff" : "#111",
            fontWeight: 600,
          }}
        >بيانات حيّة</button>
        <button
          type="button"
          data-testid="lab-source-historical-btn"
          onClick={() => onChange("historical")}
          disabled={isHist}
          style={{
            padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
            border: "1px solid var(--border)",
            background: isHist ? "#b45309" : "#fff",
            color: isHist ? "#fff" : "#111",
            fontWeight: 600,
          }}
        >عرض تاريخي</button>
      </div>
      <MetricExplainer
        category="sources"
        metricId="live_vs_historical"
        isOpen={showExplain}
        onClose={() => setShowExplain(false)}
      />
    </div>
  );
}


function DomView({ data, coverage, onLaunched, source, onSourceChange }) {
  const [openCtx, setOpenCtx] = useState(null);
  if (!data) return (
    <div className="card" data-testid="lab-dom">
      {onSourceChange && (
        <LabSourceToggle source={source} onChange={onSourceChange} meta={null} />
      )}
      جاري التحليل…
    </div>
  );
  return (
    <div className="card" data-testid="lab-dom">
      {onSourceChange && (
        <LabSourceToggle source={source} onChange={onSourceChange} meta={data.source_meta} />
      )}
      <h3 style={{ marginTop: 0 }}>📅 نمط الشهر — أثر الرواتب</h3>
      <LabBanner
        title="وش هذي الصفحة؟"
        lines={[
          "تقسم الفترة على 4 شرائح حسب يوم الشهر: <b>بداية (1-5 / رواتب)</b> · <b>ثلث أول (6-15)</b> · <b>ثلث أوسط (16-24)</b> · <b>نهاية (25-31)</b>.",
          "لكل شريحة: متوسط الإيراد اليومي + متوسط الفواتير. <b>الشريحة الأقوى</b> = أفضل وقت للحملات upsell. <b>الشريحة الأضعف</b> = تحتاج عروض اقتصادية لجذب الزائرين.",
        ]}
        actionHint="اضغط <b>📩 إنشاء حملة</b> لأي شريحة لإرسال رسالة WhatsApp لعملاء تلك الفترة. الشرائح المغطّاة بحملة نشطة تتحوّل لـ ℹ️ تلقائياً."
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
        {data.buckets.map((b) => {
          const isBest = data.best_bucket?.key === b.key;
          const isWorst = data.worst_bucket?.key === b.key;
          const coverageKey = `dom:${b.key}`;
          return (
            <div key={b.key} style={{ padding: 12, borderRadius: 8, border: `2px solid ${isBest ? "var(--success)" : isWorst ? "var(--danger)" : "var(--border)"}`, background: "#fff" }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{b.label}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>{b.days_in_bucket} يوم في الفترة · {fm(b.phones?.length || 0)} عميل فريد</div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div><div style={{ fontSize: 18, fontWeight: 800 }}>{fm(b.avg_daily_rev)} ر</div><div style={{ fontSize: 10, color: "var(--muted)" }}>متوسط يومي</div></div>
                <div style={{ textAlign: "left" }}><div style={{ fontSize: 14, fontWeight: 700 }}>{fm(b.avg_daily_invoices)}</div><div style={{ fontSize: 10, color: "var(--muted)" }}>فاتورة/يوم</div></div>
              </div>
              {isBest && <div style={{ marginTop: 6, fontSize: 11, color: "var(--success)", fontWeight: 700 }}>⭐ الأقوى — وقّت حملات upsell هنا</div>}
              {isWorst && <div style={{ marginTop: 6, fontSize: 11, color: "var(--danger)", fontWeight: 700 }}>⚠️ الأضعف — جرّب عروض اقتصادية</div>}
              <div style={{ marginTop: 8 }}>
                {(b.phones?.length || 0) >= 3 ? (
                  <ActionLaunchButton
                    coverageKey={coverageKey}
                    coverage={coverage}
                    testid={`dom-campaign-${b.key}`}
                    onClick={() => setOpenCtx({
                      kind: "dom_payday",
                      coverage_key: coverageKey,
                      coverage_label: b.label.replace(/[💸📉🤍☕]/g, "").trim(),
                      title: `📩 حملة — ${b.label}`,
                      subtitle: `${b.phones?.length || 0} عميل فريد زاروا في هذه الفترة · متوسط الإيراد اليومي ${fm(b.avg_daily_rev)} ر`,
                      phones: b.phones || [],
                      ai_ctx: { bucket_label: b.label, avg_daily_rev: b.avg_daily_rev, days: b.days_in_bucket },
                      default_message_builder: () => domDefaultMsg(b.label.replace(/[💸📉🤍☕]/g, "").trim()),
                    })}
                  />
                ) : (
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>عدد العملاء غير كافٍ</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {openCtx && <LaunchCampaignModal ctx={openCtx} onClose={() => setOpenCtx(null)} onLaunched={onLaunched} />}
    </div>
  );
}

/* ─────────── LAB: Visit Clusters ─────────── */
function ClustersView({ data, coverage, onLaunched, source, onSourceChange }) {
  const [openCtx, setOpenCtx] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(-1);

  if (!data) return (
    <div className="card" data-testid="lab-clusters">
      {onSourceChange && (
        <LabSourceToggle source={source} onChange={onSourceChange} meta={null} />
      )}
      جاري التحليل…
    </div>
  );

  const copyPhones = async (phones, idx) => {
    try {
      await navigator.clipboard.writeText((phones || []).join("\n"));
      setCopiedIdx(idx);
      toast(`تم نسخ ${phones?.length || 0} رقم للحافظة`);
      setTimeout(() => setCopiedIdx(-1), 2000);
    } catch (_e) {
      toast("تعذّر النسخ — استخدم الحملة بدلاً من ذلك", "error");
    }
  };

  const wkNorm = (lbl) => ({ "الأحد": "sun", "الإثنين": "mon", "الاثنين": "mon", "الثلاثاء": "tue", "الأربعاء": "wed", "الخميس": "thu", "الجمعة": "fri", "السبت": "sat" }[lbl] || lbl.toLowerCase());

  return (
    <div className="card" data-testid="lab-clusters">
      {onSourceChange && (
        <LabSourceToggle source={source} onChange={onSourceChange} meta={data.source_meta} />
      )}
      <h3 style={{ marginTop: 0 }}>👥 تجمعات الزيارة الدورية</h3>

      <LabBanner
        title="وش هذي الصفحة؟"
        lines={[
          "النظام فحص فواتيرك ولقى <b>مجموعات من 3 عملاء فأكثر</b> يجون في <b>نفس اليوم + نفس الساعة</b> بشكل متكرر (مرتين أو أكثر).",
          "مثال: «<b>الجمعة 13:00 — 15 عميل</b>» = 15 عميل مختلف، كلهم زاروك يوم جمعة الساعة 1 ظهراً على الأقل مرتين. هؤلاء <b>محجوزين عقلياً</b> بطقس معيّن — رسالة موحّدة تخاطب هذا الطقس تجيب نتائج عالية.",
        ]}
        actionHint="اضغط <b>📩 إنشاء حملة</b> لأي صف لإطلاق حملة WhatsApp مخصصة، أو <b>📋 نسخ الأرقام</b> لاستخدامها يدوياً. الأزرار تختفي تلقائياً للتجمعات المغطّاة بحملة نشطة (منع تكرار)."
      />

      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
        الإجمالي: {data.total_clusters_found} تجمع — معروض أعلى {Math.min(data.clusters?.length || 0, 30)}.
      </div>
      <div className="tw" style={{ maxHeight: 540, overflow: "auto" }}>
        <table>
          <thead><tr>
            <th>اليوم/الساعة</th>
            <th>عدد الأعضاء</th>
            <th>متوسط الزيارات</th>
            <th>قوة التجمع</th>
            <th>إجراء</th>
          </tr></thead>
          <tbody>
            {(data.clusters || []).map((c, i) => {
              const coverageKey = `cluster:${wkNorm(c.weekday_label)}:${String(c.hour).padStart(2, "0")}`;
              return (
                <tr key={i} data-testid={`cluster-row-${i}`}>
                  <td><b>{c.weekday_label} {String(c.hour).padStart(2, "0")}:00</b></td>
                  <td>{fm(c.member_count)}</td>
                  <td>{c.avg_visits_per_member}×</td>
                  <td>
                    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 10,
                      background: c.member_count >= 20 ? "var(--success)" : c.member_count >= 8 ? "#fbbf24" : "#cbd5e1",
                      color: "#fff", fontSize: 11, fontWeight: 700 }}>
                      {c.member_count >= 20 ? "قوي جداً" : c.member_count >= 8 ? "متوسط" : "صغير"}
                    </span>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      data-testid={`cluster-copy-${i}`}
                      className="btn btn-secondary"
                      style={{ fontSize: 11, padding: "4px 8px", marginInlineEnd: 6 }}
                      onClick={() => copyPhones(c.phones, i)}
 
                     aria-label={`نسخ ${c.phones?.length || 0} رقم`}>
                      {copiedIdx === i ? "✓ تم النسخ" : "📋 نسخ الأرقام"}
                    </button>
                    <ActionLaunchButton
                      coverageKey={coverageKey}
                      coverage={coverage}
                      testid={`cluster-campaign-${i}`}
                      onClick={() => setOpenCtx({
                        kind: "cluster",
                        coverage_key: coverageKey,
                        coverage_label: `تجمع ${c.weekday_label} ${String(c.hour).padStart(2, "0")}:00`,
                        title: `📩 حملة WhatsApp — تجمع ${c.weekday_label} ${String(c.hour).padStart(2, "0")}:00`,
                        subtitle: `المستلمون: ${c.member_count} عميل · متوسط زياراتهم في هذا التوقيت: ${c.avg_visits_per_member}×`,
                        phones: c.phones || [],
                        ai_ctx: { weekday_label: c.weekday_label, hour: c.hour, member_count: c.member_count, avg_visits_per_member: c.avg_visits_per_member },
                        default_message_builder: () => clusterDefaultMsg(c.weekday_label, c.hour),
                      })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openCtx && (
        <LaunchCampaignModal
          ctx={openCtx}
          onClose={() => setOpenCtx(null)}
          onLaunched={onLaunched}
        />
      )}
    </div>
  );
}

// ── Default message builders per context ──
function hourToArabic(h) {
  h = Number(h);
  if (h === 0) return "منتصف الليل";
  if (h < 12) return `الساعة ${h} ${h >= 5 ? "صباحاً" : "فجراً"}`;
  if (h === 12) return "الساعة 12 ظهراً";
  if (h < 17) return `الساعة ${h - 12} ظهراً`;
  if (h < 21) return `الساعة ${h - 12} مساءً`;
  return `الساعة ${h - 12} ليلاً`;
}
function clusterDefaultMsg(weekday, hour) {
  return `أهلاً {name}! اعتدنا نشوفك معنا كل ${weekday} ${hourToArabic(hour)} ☕ — نتشرّف بحضورك هذا الأسبوع 🤍`;
}
function lossesDefaultMsg(weekday, hour) {
  return `أهلاً {name} 🤍 — مرّت فترة من زيارتك الأولى ${weekday} ${hourToArabic(hour)}. نتمنى نشرّفك مرة ثانية ونعوّضك تجربة أرقى ☕`;
}
function atvDefaultMsg(hour) {
  return `مرحباً {name} ☕ — جرّب bundle ${hourToArabic(hour)}: مشروبك المفضّل + حلى المنزل بقهوة فاخرة. مساحتك في ثواني تنتظرك.`;
}
function domDefaultMsg(bucketLabel) {
  return `أهلاً {name} ☕ — ${bucketLabel} بداية رائعة معنا. كوبك المعتاد بانتظارك في ثواني 🤍`;
}

/* ─────────── Generic Modal: Launch WhatsApp campaign for ANY lab context ─────────── */
function LaunchCampaignModal({ ctx, onClose, onLaunched }) {
  const defaultMsg = useMemo(() => ctx.default_message_builder?.() || `أهلاً {name}! ☕`, [ctx]);
  const [message, setMessage] = useState(defaultMsg);
  const [campaignName, setCampaignName] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState(null);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [forceOverride, setForceOverride] = useState(false);

  const suggestAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/customer-lab/ai-suggest-message", {
        kind: ctx.kind,
        ctx: ctx.ai_ctx || {},
        phones: (ctx.phones || []).slice(0, 200),
      });
      if (r?.data?.message) {
        setMessage(r.data.message);
        toast("تم اقتراح رسالة جديدة بالـAI");
      }
    } catch (e) {
      toast(e?.response?.data?.detail || "تعذّر اقتراح الرسالة", "error");
    } finally { setAiBusy(false); }
  };

  const launch = async () => {
    if (!message.includes("{name}")) {
      toast("الرسالة يجب أن تحتوي {name}", "error"); return;
    }
    if (!ctx.phones || ctx.phones.length === 0) {
      toast("لا توجد أرقام في هذا السياق", "error"); return;
    }
    setLaunching(true);
    setConflictInfo(null);
    try {
      const r = await api.post("/customer-lab/launch-campaign", {
        coverage_key: ctx.coverage_key,
        coverage_label: ctx.coverage_label,
        campaign_type: ctx.kind,
        phones: ctx.phones,
        message_template: message,
        campaign_name: campaignName || undefined,
        meta: ctx.ai_ctx || {},
        force: forceOverride,
      });
      setResult(r.data);
      toast(`تم إنشاء حملة بـ${r.data.total} مستلم`);
      onLaunched?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "object") {
        setConflictInfo(detail);
      } else {
        toast(typeof detail === "string" ? detail : "تعذّر إنشاء الحملة", "error");
      }
    } finally { setLaunching(false); }
  };

  return (
    <div data-testid="launch-campaign-modal" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: zVar('extra_1000'),
      display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--bg, #fff)", borderRadius: 14, padding: 20,
        maxWidth: 720, width: "100%", maxHeight: "92vh", overflow: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>{ctx.title}</h3>
          <button data-testid="launch-modal-close" className="btn btn-secondary" onClick={onClose} style={{ padding: "4px 10px" }}>✕</button>
        </div>

        {!result ? (
          <>
            <div style={{ background: "rgba(140,95,50,0.06)", padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {ctx.subtitle}
            </div>

            {conflictInfo && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                <b>⚠️ تعارض مع حملة نشطة:</b> {conflictInfo.message}<br/>
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={forceOverride} onChange={(e) => setForceOverride(e.target.checked)} data-testid="force-override" style={{ marginInlineEnd: 6 }} />
                    تجاهل التعارض وأطلق حملة جديدة (لا يُنصح عادةً)
                  </label>
                </div>
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, display: "block" }}>اسم الحملة (اختياري)</label>
            <input
              data-testid="launch-campaign-name" type="text" value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder={ctx.coverage_label}
              style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid var(--border)", marginBottom: 12, fontSize: 13 }}
            />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 700 }}>
                نص الرسالة (<code style={{ background: "#fef3c7", padding: "1px 4px", borderRadius: 3 }}>{"{name}"}</code> = اسم العميل)
              </label>
              <button
                data-testid="launch-ai-suggest" className="btn btn-secondary"
                style={{ fontSize: 11, padding: "3px 8px" }}
                onClick={suggestAI} disabled={aiBusy}
              >
                {aiBusy ? "⏳ AI يكتب..." : "✨ اقترح بالـAI"}
              </button>
            </div>
            <textarea
              data-testid="launch-message-textarea" value={message}
              onChange={(e) => setMessage(e.target.value)} rows={4}
              style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid var(--border)", fontSize: 13, fontFamily: "inherit", marginBottom: 8 }}
            />

            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 10, fontSize: 12, marginBottom: 14 }}>
              <b>معاينة:</b><br/>
              <span style={{ color: "#166534" }}>{message.replace("{name}", "محمد")}</span>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={launching}>إلغاء</button>
              <button
                data-testid="launch-confirm-btn" className="btn btn-primary"
                onClick={launch} disabled={launching || !message.trim()}
              >
                {launching ? "⏳ جارٍ الإنشاء..." : `🚀 إطلاق الحملة (${ctx.phones?.length || 0} عميل)`}
              </button>
            </div>
          </>
        ) : (
          <ClusterBlastResult result={result} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function ClusterBlastResult({ result, onClose }) {
  const [sent, setSent] = useState(new Set());
  const total = result.recipients?.length || 0;
  const handleClick = (idx) => {
    setSent((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };
  return (
    <div data-testid="cluster-blast-result">
      <div style={{ background: "#d1fae5", border: "1px solid #10b981", borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 13 }}>
        ✅ تم إنشاء الحملة بنجاح. اضغط زر <b>«📲 إرسال»</b> لكل عميل لفتح WhatsApp برسالة جاهزة. تم إرسال {sent.size} من {total}.
      </div>
      <div style={{ maxHeight: 380, overflow: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
        <table className="tw" style={{ width: "100%" }}>
          <thead><tr><th>الاسم</th><th>الجوال</th><th>إرسال</th></tr></thead>
          <tbody>
            {(result.recipients || []).map((r, i) => (
              <tr key={i} style={{ background: sent.has(i) ? "#f0fdf4" : undefined }}>
                <td>{r.name}</td>
                <td style={{ direction: "ltr", textAlign: "left", fontFamily: "monospace", fontSize: 12 }}>{r.phone}</td>
                <td>
                  <a
                    href={r.wa_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleClick(i)}
                    className="btn btn-primary"
                    data-testid={`cluster-send-${i}`}
                    style={{ fontSize: 11, padding: "3px 10px", textDecoration: "none", display: "inline-block",
                      background: sent.has(i) ? "var(--success)" : undefined }}
                  >
                    {sent.has(i) ? "✓ مُرسل" : "📲 إرسال"}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>
        💡 الحملة محفوظة في «الحملات». ستظهر في نظام التتبع تلقائياً.
      </div>
      <div style={{ marginTop: 12, textAlign: "left" }}>
        <button className="btn btn-primary" onClick={onClose}>تم</button>
      </div>
    </div>
  );
}


/* ─────────── LAB: Lost First Impressions ─────────── */
function LossesView({ data, coverage, onLaunched }) {
  const [openCtx, setOpenCtx] = useState(null);
  if (!data) return <div className="card">جاري التحليل…</div>;

  const wkNorm = (lbl) => ({ "الأحد": "sun", "الإثنين": "mon", "الاثنين": "mon", "الثلاثاء": "tue", "الأربعاء": "wed", "الخميس": "thu", "الجمعة": "fri", "السبت": "sat" }[lbl] || lbl.toLowerCase());

  const renderSlot = (s, i, kind) => {
    const coverageKey = `losses:${wkNorm(s.weekday_label)}:${String(s.hour).padStart(2, "0")}`;
    const phones = s.lost_phones || [];
    return (
      <div key={`${kind}-${i}`} style={{ padding: 8, marginBottom: 6, background: kind === "worst" ? "#fef2f2" : "#f0fdf4", borderRadius: 6, fontSize: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
          <div>
            <b>{s.weekday_label} {String(s.hour).padStart(2, "0")}:00</b> →
            <span style={{ color: kind === "worst" ? "var(--danger)" : "var(--success)", fontWeight: 700 }}> {s.return_rate_pct}% عودة</span>
            <div style={{ fontSize: 10, color: "var(--muted)" }}>{s.first_visitors} زائر جديد، رجع منهم {s.returned_within_30d}، فقدنا {s.lost_count}</div>
          </div>
          {kind === "worst" && phones.length >= 3 && (
            <ActionLaunchButton
              coverageKey={coverageKey}
              coverage={coverage}
              testid={`losses-campaign-${i}`}
              label="🎯 حملة استرداد"
              onClick={() => setOpenCtx({
                kind: "losses_recovery",
                coverage_key: coverageKey,
                coverage_label: `استرداد ${s.weekday_label} ${String(s.hour).padStart(2, "0")}:00`,
                title: `🎯 حملة استرداد — ${s.weekday_label} ${String(s.hour).padStart(2, "0")}:00`,
                subtitle: `${phones.length} زائر جديد لم يعد · نسبة العودة في هذا التوقيت ${s.return_rate_pct}% (منخفضة)`,
                phones,
                ai_ctx: { weekday_label: s.weekday_label, hour: s.hour, return_rate_pct: s.return_rate_pct, lost_count: s.lost_count },
                default_message_builder: () => lossesDefaultMsg(s.weekday_label, s.hour),
              })}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card" data-testid="lab-losses">
      <h3 style={{ marginTop: 0 }}>💔 الانطباع الأول الذي يخسر العملاء</h3>
      <LabBanner
        title="وش هذي الصفحة؟"
        lines={[
          "لكل <b>(يوم × ساعة)</b>، نقيس: من جاء أول مرة في هذا التوقيت، <b>كم نسبة منهم عاد خلال 30 يوم؟</b>",
          "إذا الجواب منخفض (مثلاً 10% بدل 40%)، فهذا التوقيت <b>يحرق</b> الزوار الجدد — مؤشر مشكلة في الجودة أو الخدمة (موظف، انتظار، ضوضاء…).",
          "النظام يجمع لك <b>الجوّالات الفعلية</b> للزوار الذين لم يعودوا → جاهزة لحملة استرداد دافئة.",
        ]}
        actionHint="اضغط <b>🎯 حملة استرداد</b> بجانب أي ساعة سيئة لإرسال رسالة ودودة تذكّرهم وتعطيهم فرصة ثانية. الحملات النشطة لا تتكرر."
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
        <Kpi label="زوار جدد إجمالي" value={fm(data.overall.first_visitors)} />
        <Kpi label="عادوا خلال 30 يوم" value={fm(data.overall.returned_within_30d)} color="var(--success)" />
        <Kpi label="نسبة العودة الإجمالية" value={`${data.overall.overall_return_rate_pct}%`}
          color={data.overall.overall_return_rate_pct >= 40 ? "var(--success)" : "var(--warn)"} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <h4 style={{ color: "var(--danger)" }}>🚨 أسوأ الأوقات (انتباه!)</h4>
          {(data.worst_slots || []).map((s, i) => renderSlot(s, i, "worst"))}
        </div>
        <div>
          <h4 style={{ color: "var(--success)" }}>⭐ أفضل الأوقات</h4>
          {(data.best_slots || []).map((s, i) => renderSlot(s, i, "best"))}
        </div>
      </div>
      {openCtx && <LaunchCampaignModal ctx={openCtx} onClose={() => setOpenCtx(null)} onLaunched={onLaunched} />}
    </div>
  );
}



/* ─────────── LAB: Shift Performance ─────────── */
function ShiftsView({ data }) {
  if (!data) return <div className="card">جاري التحليل…</div>;
  const shifts = data.shifts || [];
  const best = [...shifts].filter((s) => s.first_visitors >= 5).sort((a, b) => b.return_rate_pct - a.return_rate_pct)[0];
  const worst = [...shifts].filter((s) => s.first_visitors >= 5)[0];
  return (
    <div className="card" data-testid="lab-shifts">
      <h3 style={{ marginTop: 0 }}>🧑‍🍳 أداء الورديات</h3>
      <LabBanner
        title="وش هذي الصفحة؟"
        lines={[
          "تربط <b>وردية الفاتورة</b> (صباحية / مسائية / ليلية) بـ<b>نسبة عودة الزوار الجدد خلال 30 يوم</b> لكل وردية.",
          "إذا وردية تخسر زوار جدد أكثر من غيرها، فهذا مؤشر <b>جودة خدمة</b> في تلك الوردية — موظف يحتاج تدريب، ضغط أوقات، أو مشكلة معدّات.",
          "النظام يعرض لك <b>فريق الباريستا</b> المُسنَد لكل وردية لتسهيل المتابعة الإدارية.",
        ]}
        actionHint="هذا التبويب <b>تشغيلي بحت</b> (وليس حملات WhatsApp). الإجراءات هنا: <b>تدريب موظفي الوردية الأضعف</b>، أو إعادة توزيع الفريق. اضغط على تفاصيل كل وردية لرؤية الفريق."
      />
      {worst && best && worst.shift !== best.shift && (
        <div style={{ marginBottom: 12, padding: 10, background: "#fef3c7", borderRadius: 6, fontSize: 12 }}>
          ⚠️ <b>{worst.shift_label_ar || worst.shift} تفقد عملاء أكثر</b> ({worst.return_rate_pct}% عودة) مقارنة بـ {best.shift_label_ar || best.shift} ({best.return_rate_pct}%). فرق {(best.return_rate_pct - worst.return_rate_pct).toFixed(1)} نقطة.
        </div>
      )}
      <div style={{ display: "grid", gap: 12 }}>
        {shifts.map((s) => {
          const isWorst = worst && s.shift === worst.shift;
          const isBest = best && s.shift === best.shift && best !== worst;
          return (
            <div key={s.shift} style={{
              padding: 12, borderRadius: 8,
              border: `2px solid ${isWorst ? "var(--danger)" : isBest ? "var(--success)" : "var(--border)"}`,
              background: "#fff",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h4 style={{ margin: 0 }}>
                  {s.shift_label_ar || s.shift}
                  {isWorst && <span style={{ marginInlineStart: 8, fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "var(--danger)", color: "#fff" }}>الأضعف</span>}
                  {isBest && <span style={{ marginInlineStart: 8, fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "var(--success)", color: "#fff" }}>الأفضل</span>}
                </h4>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.return_rate_pct >= 40 ? "var(--success)" : s.return_rate_pct >= 30 ? "var(--warn)" : "var(--danger)" }}>
                    {s.return_rate_pct}%
                  </div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>نسبة العودة</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, fontSize: 12 }}>
                <div><span style={{ color: "var(--muted)" }}>الإيراد:</span> <b>{fm(s.revenue_in_range)} ر</b></div>
                <div><span style={{ color: "var(--muted)" }}>فواتير:</span> <b>{fm(s.invoices_in_range)}</b></div>
                <div><span style={{ color: "var(--muted)" }}>زوار جدد:</span> <b>{fm(s.first_visitors)}</b></div>
                <div><span style={{ color: "var(--muted)" }}>عادوا:</span> <b>{fm(s.returned_within_30d)}</b></div>
              </div>
              {s.team_size > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    ☕ فريق الباريستا ({s.team_size})
                  </summary>
                  <div className="tw" style={{ marginTop: 6 }}>
                    <table>
                      <thead><tr><th>الاسم</th><th>الفرع</th></tr></thead>
                      <tbody>
                        {s.team.map((m, i) => (
                          <tr key={i}><td>{m.name || "—"}</td><td>{m.branch || "—"}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ─────────── 1. Segments ─────────── */
function SegmentsSection({ data }) {
  return (
    <div className="card" data-testid="lab-segments">
      <h3 style={{ marginTop: 0 }}>📊 تجزئة العملاء — {data.date_from} → {data.date_to}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 14 }}>
        <Kpi label="عملاء فريدون" value={fm(data.total_customers)} />
        <Kpi label="فواتير" value={fm(data.total_invoices)} />
        <Kpi label="إجمالي الإنفاق" value={`${fm(data.total_spent)} ر`} />
        <Kpi label="نسبة العودة" value={`${data.repeat_rate_pct}%`} color={data.repeat_rate_pct >= 30 ? "var(--success)" : "var(--warn)"} />
        <Kpi label="عائدون (≥2 زيارة)" value={fm(data.repeat_customers)} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
        {data.buckets.map((b) => (
          <div key={b.key} style={{ padding: 12, borderRadius: 8, border: "1px solid var(--border)", background: "#fff" }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{b.label}</div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>{b.desc}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div><b style={{ fontSize: 22 }}>{fm(b.count)}</b><span style={{ fontSize: 11, color: "var(--muted)" }}> عميل</span></div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>{b.spent_share_pct}% من الإيراد</div>
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--muted)" }}>إنفاق إجمالي: {fm(b.spent)} ر</div>
          </div>
        ))}
      </div>
      {data.top_10_spenders?.length > 0 && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>👑 أعلى 10 منفقين في الفترة</summary>
          <div className="tw" style={{ marginTop: 8 }}>
            <table>
              <thead><tr><th>#</th><th>الاسم</th><th>زيارات</th><th>متوسط الفاتورة</th><th>إجمالي</th></tr></thead>
              <tbody>
                {data.top_10_spenders.map((c, i) => (
                  <tr key={c.phone}>
                    <td>{i + 1}</td><td>{c.name}</td><td>{c.visits}</td>
                    <td>{fm(c.atv)} ر</td><td><b>{fm(c.spent)} ر</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div style={{ padding: 10, borderRadius: 8, background: "var(--beige)", textAlign: "center" }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || "var(--text)" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--muted)" }}>{label}</div>
    </div>
  );
}

/* ─────────── 2. Hour Heatmap ─────────── */
function HeatmapSection({ data }) {
  const [mode, setMode] = useState("daily"); // "weekly" | "daily"
  // Find max rev for color scaling
  const maxRev = useMemo(() => {
    let m = 0;
    if (mode === "weekly") {
      for (const row of data.grid) for (const c of row) if (c.rev > m) m = c.rev;
    } else {
      for (const d of data.daily || []) for (const c of d.cells) if (c.rev > m) m = c.rev;
    }
    return m || 1;
  }, [data, mode]);
  const cellColor = (rev) => {
    if (rev === 0) return "#f1ede5";
    const r = rev / maxRev;
    if (r >= 0.65) return "#16a34a";
    if (r >= 0.35) return "#fbbf24";
    if (r > 0) return "#f87171";
    return "#f1ede5";
  };
  return (
    <div className="card" data-testid="lab-heatmap">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ marginTop: 0, marginBottom: 4 }}>🔥 خريطة الإيراد بالساعة</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span data-testid="heatmap-source-badge" style={{
            fontSize: 10.5, fontWeight: 800, padding: "4px 10px", borderRadius: 999,
            background: data.source === "live" ? "rgba(46,139,87,0.12)" : "rgba(217,119,6,0.12)",
            color: data.source === "live" ? "#2e8b57" : "#b45309",
            border: `1px solid ${data.source === "live" ? "rgba(46,139,87,0.35)" : "rgba(217,119,6,0.35)"}`,
          }}>
            {data.source === "live" ? "🟢 بيانات RETM حية · توقيت الرياض" : "📄 من الملف المرفوع"}
          </span>
          <div style={{ display: "flex", gap: 4, padding: 2, background: "var(--beige)", borderRadius: 6 }}>
          <button onClick={() => setMode("daily")} data-testid="heatmap-mode-daily"
            style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: mode === "daily" ? "var(--accent)" : "transparent", color: mode === "daily" ? "#fff" : "var(--text)", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            📅 يوم بتاريخه
          </button>
          <button onClick={() => setMode("weekly")} data-testid="heatmap-mode-weekly"
            style={{ padding: "4px 12px", borderRadius: 4, border: "none", background: mode === "weekly" ? "var(--accent)" : "transparent", color: mode === "weekly" ? "#fff" : "var(--text)", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            📊 نمط أسبوعي
          </button>
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10, marginTop: 6 }}>
        {mode === "daily"
          ? `كل يوم بتاريخه مع ساعاته — لتشخيص ساعة معينة في يوم محدد. الإجمالي: ${fm(data.grand_revenue)} ر / ${fm(data.grand_invoices)} فاتورة.`
          : `النمط الأسبوعي العام (كل الخميسات معاً، كل الجمعات معاً…). أخضر=قوي · أصفر=متوسط · أحمر=ضعيف.`}
      </div>
      {mode === "weekly" ? (
        <WeeklyHeatmap data={data} maxRev={maxRev} cellColor={cellColor} />
      ) : (
        <DailyHeatmap data={data} maxRev={maxRev} cellColor={cellColor} />
      )}
      {/* v119.217 — Clear legend: color thresholds + zero meaning */}
      <div data-testid="heatmap-legend" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12, fontSize: 11, fontWeight: 700, alignItems: "center" }}>
        {[["#16a34a", "قوي (≥65% من الأعلى)"], ["#fbbf24", "متوسط (35-65%)"], ["#f87171", "منخفض (>0)"], ["#f1ede5", "0 = لا مبيعات بهذه الساعة"]].map(([c, l]) => (
          <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--muted)" }}>
            <i style={{ width: 14, height: 14, borderRadius: 4, background: c, display: "inline-block", border: "1px solid rgba(0,0,0,0.08)" }} /> {l}
          </span>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
        <div style={{ padding: 10, background: "rgba(46,139,87,0.08)", border: "1px solid rgba(46,139,87,0.25)", borderRadius: 8, fontSize: 12 }}>
          ✅ <b>ساعات قوية:</b>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
            {data.strong_hours.length ? data.strong_hours.map((h) => (
              <span key={h} style={{ background: "#16a34a", color: "#fff", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 800, direction: "ltr" }}>{String(h).padStart(2, "0")}:00</span>
            )) : "—"}
          </div>
        </div>
        <div style={{ padding: 10, background: "rgba(185,83,62,0.08)", border: "1px solid rgba(185,83,62,0.25)", borderRadius: 8, fontSize: 12 }}>
          ⚠️ <b>ساعات ضعيفة:</b>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
            {data.weak_hours.length ? data.weak_hours.map((h) => (
              <span key={h} style={{ background: "#f87171", color: "#fff", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 800, direction: "ltr" }}>{String(h).padStart(2, "0")}:00</span>
            )) : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function WeeklyHeatmap({ data, cellColor, maxRev }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>
            <th style={thStyle}>ساعة</th>
            {data.weekday_labels.map((d) => <th key={d} style={thStyle}>{d}</th>)}
            <th style={{ ...thStyle, background: "#7c3aed", color: "#fff" }}>المجموع</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, h) => h).map((h) => (
            <tr key={h}>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{String(h).padStart(2, "0")}:00</td>
              {data.grid.map((row, wd) => {
                const c = row[h];
                return (
                  <td key={wd}
                    style={{ ...tdStyle, background: cellColor(c.rev), color: c.rev > maxRev * 0.5 ? "#fff" : c.n ? "#333" : "#c2b8a8", fontWeight: 600 }}><InfoTip text={`${data.weekday_labels[wd]} ${h}:00 → ${fm(c.rev)} ر / ${c.n} فاتورة`} />
                    {c.n ? fm(c.rev) : "0"}
                  </td>
                );
              })}
              <td style={{ ...tdStyle, background: "#faf5e8", fontWeight: 700 }}>{fm(data.hour_totals[h].rev)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyHeatmap({ data, cellColor, maxRev }) {
  const days = data.daily || [];
  if (days.length === 0) {
    return <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>لا فواتير في الفترة المختارة</div>;
  }
  return (
    <div style={{ overflowX: "auto", maxHeight: 540 }}>
      <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, position: "sticky", insetInlineEnd: 0, zIndex: zVar('extra_3') }}>ساعة \\ التاريخ</th>
            {days.map((d) => (
              <th key={d.date} style={{ ...thStyle, fontSize: 10 }}>
                <div>{d.weekday_label}</div>
                <div style={{ opacity: 0.85, fontWeight: 400 }}>{d.date.slice(5)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, h) => h).map((h) => (
            <tr key={h}>
              <td style={{ ...tdStyle, fontWeight: 700, position: "sticky", insetInlineEnd: 0, background: "#5b4534", color: "#fff", zIndex: zVar('extra_2') }}>
                {String(h).padStart(2, "0")}:00
              </td>
              {days.map((d) => {
                const c = d.cells[h];
                return (
                  <td key={d.date}
                    style={{ ...tdStyle, background: cellColor(c.rev), color: c.rev > maxRev * 0.5 ? "#fff" : c.n ? "#333" : "#c2b8a8", fontWeight: 600, minWidth: 64 }}><InfoTip text={`${d.weekday_label} ${d.date} ${h}:00 → ${fm(c.rev)} ر / ${c.n} فاتورة`} />
                    {c.n ? fm(c.rev) : "0"}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr style={{ borderTop: "2px solid #5b4534" }}>
            <td style={{ ...tdStyle, fontWeight: 800, background: "#7c3aed", color: "#fff", position: "sticky", insetInlineEnd: 0, zIndex: zVar('extra_2') }}>
              مجموع اليوم
            </td>
            {days.map((d) => (
              <td key={d.date} style={{ ...tdStyle, background: "#faf5e8", fontWeight: 800 }}>
                {fm(d.day_total_rev)}<br/>
                <span style={{ fontSize: 9, fontWeight: 400, color: "var(--muted)" }}>{d.day_total_n} ف</span>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
const thStyle = { padding: "6px 10px", background: "#5b4534", color: "#fff", border: "1px solid #fff", whiteSpace: "nowrap" };
const tdStyle = { padding: "5px 8px", border: "1px solid #fff", textAlign: "center", minWidth: 56 };
